// Pure helpers for the Daily-View priority + fill-ticket-coverage filters.
//
// Coverage is an item-code-level question: an order line is "covered" when an
// OPEN fill ticket exists for its SKU's item code (open_fill_tickets, keyed by
// item_code; there is no order<->ticket key).
//
// Priority ranks by the model's per-order `orderScore` when the run carries a
// differentiated score map (2026-07+ solves recompute it in-model — staged /
// MTO / days-old tiers — and emit it as the `order_scores` result table,
// surfaced via ?enrich=scores). Ties, and runs without differentiated scores
// (older solves saw a uniform 1.0 input), fall back to critical-PO first, then
// ORDER SIZE (shipped lbs) — the pre-rework behaviour.

import type {
  OpenFillTicketMap,
  OpenFillTicketSummary,
  OrderScoreMap,
} from '../../../services/cplex';
import type { DailyViewFilterState, MPPRow } from './types';

const DROPPED = 'Dropped / Unfilled';

/** Sage item code parsed from an MPP `sku` ("Display Name (CODE)"). */
export function itemCodeOf(sku: string | null | undefined): string | null {
  if (!sku) return null;
  const m = String(sku).match(/\(([^)]+)\)\s*$/);
  return m ? m[1].trim() : null;
}

/** The open-ticket summary covering a row's item code, or null. */
export function coverageOf(
  row: MPPRow,
  tickets: OpenFillTicketMap | null | undefined,
): OpenFillTicketSummary | null {
  if (!tickets) return null;
  const code = itemCodeOf(row.sku);
  if (!code) return null;
  return tickets[code] ?? null;
}

export function isCovered(
  row: MPPRow,
  tickets: OpenFillTicketMap | null | undefined,
): boolean {
  return coverageOf(row, tickets) != null;
}

/**
 * True when the score map differentiates the given orders (some non-tied
 * value to rank by). A uniform map — e.g. an older run's flat-1.0 input —
 * carries no ranking signal, so callers fall back to size + critical.
 */
export function hasDifferentiatedScores(
  scores: OrderScoreMap | null | undefined,
  orders: Iterable<string>,
): boolean {
  if (!scores) return false;
  let first: number | null = null;
  for (const o of orders) {
    const s = scores[o];
    if (typeof s !== 'number') continue;
    if (first === null) first = s;
    else if (s !== first) return true;
  }
  return false;
}

/**
 * Narrow a model+day slice by the priority + coverage controls. Order:
 * critical-only → coverage → top-N (by the model's orderScore when the run
 * carries differentiated scores; critical-first-then-size otherwise and as
 * the tiebreak). Returns a new array; the input is never mutated. With the
 * default (all / 0 / off) state this is an identity pass, so the page
 * behaves exactly as before until used.
 */
export function applyDailyViewFilters(
  rows: readonly MPPRow[],
  state: DailyViewFilterState,
  tickets: OpenFillTicketMap | null | undefined,
  orderScores?: OrderScoreMap | null,
): MPPRow[] {
  let out = rows.slice();

  if (state.criticalOnly) {
    const criticalOrders = new Set(
      out.filter((r) => r.critical_po === 'Yes').map((r) => r.order),
    );
    out = out.filter((r) => r.order != null && criticalOrders.has(r.order));
  }

  if (state.coverage === 'covered') {
    out = out.filter((r) => isCovered(r, tickets));
  } else if (state.coverage === 'missing') {
    out = out.filter((r) => !isCovered(r, tickets));
  }

  if (state.topN > 0) {
    // Rank at the ORDER grain: by the model's orderScore when differentiated,
    // then critical POs, then total shipped lbs (also the full order for
    // score-less / older runs).
    const lbsByOrder = new Map<string, number>();
    const criticalByOrder = new Map<string, boolean>();
    for (const r of out) {
      if (!r.order) continue;
      lbsByOrder.set(r.order, (lbsByOrder.get(r.order) ?? 0) + (Number(r.s_to_o) || 0));
      if (r.critical_po === 'Yes') criticalByOrder.set(r.order, true);
    }
    const useScores = hasDifferentiatedScores(orderScores, lbsByOrder.keys());
    const ranked = Array.from(lbsByOrder.keys()).sort((a, b) => {
      if (useScores) {
        const sa = orderScores?.[a] ?? 0;
        const sb = orderScores?.[b] ?? 0;
        if (sa !== sb) return sb - sa;
      }
      const ca = criticalByOrder.get(a) ? 1 : 0;
      const cb = criticalByOrder.get(b) ? 1 : 0;
      if (ca !== cb) return cb - ca;
      return (lbsByOrder.get(b) ?? 0) - (lbsByOrder.get(a) ?? 0);
    });
    const top = new Set(ranked.slice(0, state.topN));
    out = out.filter((r) => r.order != null && top.has(r.order));
  }

  return out;
}

export interface CoverageStats {
  covered: number; // distinct order×item lines (planned) with an open ticket
  missing: number; // distinct order×item lines (planned) with NO open ticket
}

/**
 * Coverage counts over a slice, at the distinct (order, item_code) grain so the
 * multi-stage MPP rows of one order line don't inflate the totals. Only planned
 * (non-dropped) lines count — a model-dropped line isn't a ticket you'd create.
 */
export function coverageStats(
  rows: readonly MPPRow[],
  tickets: OpenFillTicketMap | null | undefined,
): CoverageStats {
  const covered = new Set<string>();
  const missing = new Set<string>();
  for (const r of rows) {
    if (r.filled_from === DROPPED) continue;
    const code = itemCodeOf(r.sku);
    if (!code) continue;
    const key = `${r.order ?? ''}|${code}`;
    if (tickets && tickets[code]) covered.add(key);
    else missing.add(key);
  }
  return { covered: covered.size, missing: missing.size };
}
